import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import {
  getExecutionTemplates,
  isWordLikeTemplateType,
  type ExecutionTemplateRow,
} from '../../../projects/services/executionTemplatesService';
import { EXECUTION_TEMPLATES_CHANGED_EVENT } from '../../../projects/services/executionTemplatesEvents';
import { configService } from '../../../../shared/services/configService';
import { TemplateService } from '../../../../shared/services/templateService';
import { WordService } from '../../../../shared/services/wordService';
import {
  SaveConflictUserResolutionError,
  StorageService,
} from '../../../../shared/services/storageService';

interface ActiveExecutionTemplateSelectProps {
  executionId?: string | null;
}

const ActiveExecutionTemplateSelect: React.FC<ActiveExecutionTemplateSelectProps> = ({ executionId }) => {
  const [loading, setLoading] = useState(false);
  const [wordLikeRows, setWordLikeRows] = useState<ExecutionTemplateRow[]>([]);
  const [value, setValue] = useState<string>('');

  const syncFromServer = useCallback(async () => {
    if (!executionId) {
      setWordLikeRows([]);
      setValue('');
      configService.setActiveExecutionTemplateId(null);
      return;
    }

    setLoading(true);
    try {
      const rows = await getExecutionTemplates(executionId);
      const wordLike = rows.filter((r) => isWordLikeTemplateType(r.type));
      setWordLikeRows(wordLike);

      const active = configService.getConfig().activeExecutionTemplateId;
      const next =
        active && wordLike.some((w) => w.execution_template_id === active)
          ? active
          : wordLike[0]?.execution_template_id ?? '';

      setValue(next);
      if (next) {
        configService.setActiveExecutionTemplateId(next);
      } else {
        configService.setActiveExecutionTemplateId(null);
      }
    } catch (e) {
      console.error('[ActiveExecutionTemplateSelect]', e);
      toast.error(e instanceof Error ? e.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  useEffect(() => {
    void syncFromServer();
  }, [syncFromServer]);

  useEffect(() => {
    const onChanged = () => {
      void syncFromServer();
    };
    window.addEventListener(EXECUTION_TEMPLATES_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(EXECUTION_TEMPLATES_CHANGED_EVENT, onChanged);
  }, [syncFromServer]);

  const handleChange = async (newId: string) => {
    if (!newId || newId === value) return;
    const prev = configService.getConfig().activeExecutionTemplateId;
    const tid = toast.loading('Switching template…');
    try {
      if (prev && prev !== newId) {
        try {
          await StorageService.saveDocumentToSupabase({ throwOnConflictUserChoice: true });
        } catch (e) {
          if (e instanceof SaveConflictUserResolutionError) {
            toast.dismiss(tid);
            return;
          }
          /* best-effort before switching */
        }
      }
      configService.setActiveExecutionTemplateId(newId);
      setValue(newId);
      const downloaded = await TemplateService.downloadLatestTemplate(newId);
      if (downloaded) {
        await WordService.loadDocumentFromBlob(downloaded.blob);
        StorageService.noteLoadedExecutionTemplateHead(newId, downloaded.fileName);
        await StorageService.syncAutosaveBaselineFromWord();
        toast.success('Template loaded', { id: tid });
      } else {
        toast.message('No saved file in storage yet for this template', { id: tid });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not switch template', { id: tid });
      setValue(prev || '');
      configService.setActiveExecutionTemplateId(prev ?? null);
    }
  };

  if (!executionId) {
    return null;
  }

  if (loading && wordLikeRows.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground h-8 px-1">
        <Spinner size="sm" variant="muted" />
        Templates…
      </div>
    );
  }

  if (wordLikeRows.length === 0) {
    return (
      <div className="text-xs text-muted-foreground leading-8 px-1 truncate max-w-[200px]" title="Add a Word template">
        No Word template
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={(v) => void handleChange(v)}>
      <SelectTrigger className="h-8 w-[min(200px,42vw)] text-xs text-foreground border-border bg-white/90 shadow-none">
        <SelectValue placeholder="Template" />
      </SelectTrigger>
      <SelectContent>
        {wordLikeRows.map((r) => (
          <SelectItem key={r.execution_template_id} value={r.execution_template_id} className="text-xs">
            <span className="truncate">{r.name}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default ActiveExecutionTemplateSelect;
